import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import './TeacherVideoPlayer.css';

// Same rule as a voice note: 90% of the way through counts as watched.
const WATCHED_FRACTION = 0.9;

/**
 * Full-screen player for a teacher's video feedback on one recording.
 * `onWatched` fires once, at 90% — the video counterpart of a voice note's
 * listen threshold — or at the end: Chrome's WebM recordings report no
 * duration, so for them the end is the only signal there is.
 */
export function TeacherVideoPlayer({ url, onClose, onWatched }) {
  const firedRef = useRef(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const watched = () => {
    if (firedRef.current) return;
    firedRef.current = true;
    onWatched?.();
  };

  const handleTimeUpdate = (e) => {
    const { currentTime, duration } = e.currentTarget;
    if (Number.isFinite(duration) && duration > 0 && currentTime >= duration * WATCHED_FRACTION) watched();
  };

  return createPortal(
    // A portal still bubbles through the React tree: stop clicks here so they
    // never reach a modal underneath (the feedback mail closes on overlay click).
    <div className="tvp-overlay" onClick={(e) => { e.stopPropagation(); onClose?.(); }} data-testid="teacher-video-player">
      <div className="tvp-dialog" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="tvp-close" onClick={onClose} aria-label="关闭">
          <X size={22} />
        </button>
        {failed ? (
          // Not marked watched: the teacher should see it never reached the child.
          <p className="tvp-error">这个视频在这台设备上播放不了，请告诉老师。</p>
        ) : (
          <video
            className="tvp-video"
            src={url}
            controls
            autoPlay
            playsInline
            onTimeUpdate={handleTimeUpdate}
            onEnded={watched}
            onError={() => setFailed(true)}
          />
        )}
      </div>
    </div>,
    document.body,
  );
}
